/** Internal physics data model (a mutable ball + render frame types). */
import type { BallId, BallMotionState, BallSnapshot, Vec3 } from '../types';
import { quatIdentity, type Quat } from './vec';

export interface Ball<B extends string = BallId> {
  id: B;
  radius: number;
  mass: number;
  invMass: number;
  /** 1 / I for a solid sphere, I = (2/5) m R^2. */
  invInertia: number;
  pos: Vec3;
  vel: Vec3;
  angVel: Vec3;
  orientation: Quat;
  state: BallMotionState;
}

export function makeBall<B extends string = BallId>(id: B, radius: number, mass: number, x: number, y: number): Ball<B> {
  const inertia = (2 / 5) * mass * radius * radius;
  return {
    id,
    radius,
    mass,
    invMass: 1 / mass,
    invInertia: 1 / inertia,
    pos: { x, y, z: radius },
    vel: { x: 0, y: 0, z: 0 },
    angVel: { x: 0, y: 0, z: 0 },
    orientation: quatIdentity(),
    state: 'stationary',
  };
}

export function cloneBall<B extends string = BallId>(b: Ball<B>): Ball<B> {
  return {
    ...b,
    pos: { ...b.pos },
    vel: { ...b.vel },
    angVel: { ...b.angVel },
    orientation: { ...b.orientation },
  };
}

export function toSnapshot<B extends string = BallId>(b: Ball<B>): BallSnapshot<B> {
  return {
    id: b.id,
    radius: b.radius,
    pos: { ...b.pos },
    vel: { ...b.vel },
    angVel: { ...b.angVel },
    state: b.state,
  };
}

/** Lightweight per-ball transform for the renderer. */
export interface RenderBall {
  pos: Vec3;
  orientation: Quat;
}

export interface Frame<B extends string = BallId> {
  t: number;
  balls: Record<B, RenderBall>;
}

export type { BallMotionState };
